/* eslint-disable i18next/no-literal-string */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { mergeStyleSets, Stack } from '@fluentui/react';
import { FluentIconsProps, MoreHorizontal24Regular, QuestionCircle24Regular } from '@fluentui/react-icons';
import { useAuth } from 'AuthProvider';
import { DarkShellAppBrandTheme, LightShellAppBrandTheme, ThemeType, useCurrentThemeType } from 'common/theme';
import { isMobileScreen } from 'common/utilities';
import { isFamilySettingEnabled, isNotificationSettingEnabled, isPrivacySettingEnabled } from 'common/utilities/FlightSetting';
import { pageDataService } from 'common/utilities/PageDataService';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { Configuration } from 'Configuration';
import { getLocale, useLocalizedStrings } from 'i18n';
import { useAppSelector } from 'store';

import {
  BaseTheme,
  IO365ShellShim,
  RenderSettings,
  ShellLoadErrorEvent,
  SignOutEventArgs,
} from '@suiteux/suiteux-shell-typings/typings/suiteux-shell';

import { MenuItemKey, OfficeShellMenu } from './OfficeShellMenu';
import { OfficeShellPanel } from './OfficeShellPanel';

import './OfficeShell.Styles.scss';

const shellHost = 'https://shell.cdn.office.net';
const shellBootstrapperUrl = `${shellHost}/api/shellbootstrapper/consumer/oneshell`;

type AuthType = 'msa' | 'msa_implicit' | 'o365';

interface IOfficeShellConfig {
  userInfo: IAuthenticatedUserInfo;
  theme: BaseTheme;
  showNavigation: boolean;
}

interface IAuthenticatedUserInfo {
  name: string;
  upn: string;
  puid: string;
  authType: AuthType;
}

declare const window: {
  O365Shell?: IO365ShellShim;
  React: typeof React;
  ReactDOM: typeof ReactDOM;
} & Window;

/**
 * Helper to get typed `O365Shell` object from window
 */
export const getWindowO365Shell = () => window.O365Shell;

/**
 * Invocate {@link IO365ShellShim.RenderAsync} to render Office Shell
 *
 * @param shellLoadStart
 * @param shellConfig
 */
const renderOfficeShell = (shellLoadStart: number, shellConfig: IOfficeShellConfig): void => {
  const locale = getLocale();
  const renderSettings: RenderSettings = {
    isThinHeader: true,
    top: 'OneShellContainer',
    disableToasts: true,
    helpNamespace: 'Defender',
    initialRenderData: {
      ShellBootHost: shellHost,
      IsConsumer: true,
      WorkloadId: 'GibraltarGWS',
      AppBrandTheme: shellConfig.theme,
      UserDisplayName: shellConfig.userInfo.name,
      UPN: shellConfig.userInfo.upn,
      Language: locale,
      Culture: locale,
      UserID: shellConfig.userInfo.puid || undefined, // hide user icon button (by specifying undefined) if user is not authenticated
      CSSExperiencesEnabled: true,
    },
    shellDataOverrides: {
      AppHeaderLinkText: Configuration.FullProductName,
      MAXHelpEnabled: true,
      HelpPaneDisabled: true,
      UniversalMeControlEnabled: true,
    },
  };

  getWindowO365Shell()?.RenderAsync(
    renderSettings,
    () => console.info('Consumer shell rendered'),
    () => {
      const loadTime = Date.now() - shellLoadStart;
      TelemetryProvider.Instance.trackEvent('OfficeShellLoad', { loadTime: loadTime.toString() });

      getWindowO365Shell()?.OnSignOut((event: SignOutEventArgs) => {
        // Prevent the redirect for the default logout url
        event.triggerEvent.preventDefault();
        window.location.href = Configuration.SignOutUrl;
      });
    },
    (error: ShellLoadErrorEvent) => {
      TelemetryProvider.Instance.trackException({
        name: 'ShellLoadErrorEvent',
        message: `Consumer shell failed to render! ResourceName: ${error.ResourceName} Error message: ${error.Details}`,
      });
    }
  );
};

/**
 * Log Office Shell Bootstrapper script loading failure
 *
 * @param userInfo
 * @param cause
 */
const onShellBootstrapperFailed = (userInfo: IAuthenticatedUserInfo, cause?: Error): void => {
  TelemetryProvider.Instance.trackException({
    name: 'ShellBootstrapperFailed',
    message: `Shell bootstrapper load failed: ${userInfo.puid} (${cause?.message})`,
    stack: cause?.stack,
  });
};

/**
 * Load Office Shell Bootstrapper script
 *
 * @param shellConfig
 * @param onLoad
 */
const loadOfficeShellBootstrapper = (shellConfig: IOfficeShellConfig, onLoad: () => void): void => {
  const id = 'shell-bootstrapper';
  if (document.getElementById(id)) {
    return;
  }

  const shellLoadStart = Date.now();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'text/javascript';
  script.src = shellBootstrapperUrl;

  // The cross origin attribute is added so workloads can obtain more debugging
  // information when scripts throw an error
  script.crossOrigin = 'anonymous';

  script.onload = () => {
    renderOfficeShell(shellLoadStart, shellConfig);
    onLoad();
  };
  script.onerror = (_event: Event | string, _source?: string, _lineno?: number, _colno?: number, error?: Error) => {
    onShellBootstrapperFailed(shellConfig.userInfo, error);
  };
  document.body.appendChild(script);
};

/**
 * Load and initialize Office Shell
 *
 * @param theme
 * @param onLoad
 */
const loadOfficeShell = (theme: BaseTheme, onLoad: () => void): void => {
  // expose React and ReactDOM in the global namespace so it gets reused by the shell
  window.React = React;
  window.ReactDOM = ReactDOM;

  const userData = pageDataService.getUserData();

  const userInfo: IAuthenticatedUserInfo = {
    name: userData.displayName,
    upn: userData.userPrincipalName,
    puid: userData.userId,
    authType: 'msa',
  };

  const showNavigation = true;

  const shellConfig: IOfficeShellConfig = {
    userInfo,
    theme,
    showNavigation,
  };

  // add the script to the body and wait for it to be loaded
  loadOfficeShellBootstrapper(shellConfig, onLoad);
};

const menuButtonIds = {
  Mobile: 'mobile-shell-menu-button',
  HelpAndFeedback: 'help-and-feedback-shell-menu-button',
  SettingsAndPrivacy: 'settings-and-privacy-shell-menu-button',

  // TODO: this button appears on very small viewports
  // due to responsive logic in OneShell. It opens an empty menu.
  // We don't use it. Not sure how to disable it via the API.
  DefaultShellButton: 'O365_MainLink_Affordance',
} as const;

const containerClassNames = mergeStyleSets({
  mobile: {
    [`#${menuButtonIds.SettingsAndPrivacy}`]: {
      display: 'none',
    },
    [`#${menuButtonIds.HelpAndFeedback}`]: {
      display: 'none',
    },
    [`#${menuButtonIds.DefaultShellButton}`]: {
      display: 'none',
    },
  },
  large: {
    [`#${menuButtonIds.Mobile}`]: {
      display: 'none',
    },
  },
});

/**
 * Open (MAX) help side pane
 */
export const openHelpFlexPane = (): boolean => getWindowO365Shell()?.FlexPane.OpenFlexPaneForProvider('Help') ?? false;

export const OfficeShell: React.FC = () => {
  const isWebView = pageDataService.isWebView();
  const themeName = useCurrentThemeType();
  const { license } = useAuth();

  const { t } = useLocalizedStrings();
  const { screenSize } = useAppSelector((state) => state.layout);
  const isMobile = isMobileScreen(screenSize);

  const [activeMenu, setActiveMenu] = useState<{ menuButtonId: string; menuItemKeys: MenuItemKey[] }>();
  const onHideMenu = useCallback(() => {
    setActiveMenu(undefined);
  }, []);

  const shellTheme: BaseTheme = themeName === ThemeType.Dark ? DarkShellAppBrandTheme : LightShellAppBrandTheme;
  const shell = getWindowO365Shell();

  useEffect(() => {
    if (shell?.Theme) {
      document.getElementById('app')?.setAttribute('about', themeName.toString());
      shell.Theme.SetAppBrandTheme(shellTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName]);

  // show subscription button (with divider above) if user has an active subscription
  const subscriptionMenuItems = useMemo(
    () => (license?.isSubscriptionActive ? (['_Divider', 'Subscription'] as const) : []),
    [license?.isSubscriptionActive]
  );

  // whether user has passed license check
  const passedLicenseCheck = !!(license && (!license.isLicenseRequired || license.isSubscriptionActive));

  // define the Office Shell menus and menu items
  const menuButtonConfigs = useMemo(() => {
    const configs: {
      id: (typeof menuButtonIds)[keyof typeof menuButtonIds];
      icon: React.FC<FluentIconsProps>;
      menuItemKeys: MenuItemKey[];
      label: string;
    }[] = [
      {
        id: menuButtonIds.Mobile,
        icon: MoreHorizontal24Regular,
        menuItemKeys: [
          passedLicenseCheck && isNotificationSettingEnabled() && 'Settings_Notification',
          passedLicenseCheck && isFamilySettingEnabled() && 'Settings_FamilySharing',
          passedLicenseCheck && isPrivacySettingEnabled() && 'Privacy',
          'Help',
          'Feedback_Bug',
          'Feedback_Like',
          'Feedback_Suggestion',
          ...subscriptionMenuItems,
        ],
        label: t('Settings'),
      },
      {
        id: menuButtonIds.HelpAndFeedback,
        icon: QuestionCircle24Regular,
        menuItemKeys: ['Help', '_Divider', 'Feedback_Bug', 'Feedback_Like', 'Feedback_Suggestion'],
        label: t('HelpAndFeedback'),
      },
      {
        id: menuButtonIds.SettingsAndPrivacy,
        icon: MoreHorizontal24Regular,
        menuItemKeys: [
          passedLicenseCheck && isNotificationSettingEnabled() && 'Settings_Notification',
          passedLicenseCheck && isFamilySettingEnabled() && 'Settings_FamilySharing',
          passedLicenseCheck && isPrivacySettingEnabled() && 'Privacy',
          ...subscriptionMenuItems,
        ],
        label: t('Settings'),
      },
    ];

    return configs;
  }, [passedLicenseCheck, subscriptionMenuItems, t]);

  // open an Office Shell menu
  const toggleMenu = useCallback(
    (menuButtonId: string, menuItemKeys: MenuItemKey[]) =>
      setActiveMenu((currentActiveMenu) => {
        if (currentActiveMenu?.menuButtonId === menuButtonId) {
          return undefined;
        }
        return {
          menuButtonId,
          menuItemKeys,
        };
      }),
    []
  );

  // whether Office Shell has been loaded (i.e. `window.OfficeShell` is available)
  const [isShellReady, setIsShellReady] = useState(false);

  // update Office Shell menus
  useEffect(() => {
    if (isShellReady) {
      // add menu buttons to shell
      menuButtonConfigs.forEach((config) => {
        if (config.menuItemKeys.filter((key) => key).length > 0) {
          getWindowO365Shell()?.Extensibility.AddOrUpdateCustomHeaderButton({
            id: config.id,
            iconData: {
              element: (
                <Stack horizontalAlign="center" verticalAlign="center">
                  <config.icon />
                </Stack>
              ),
            },
            onHide: () => {}, // not applicable when isStateless is true
            onShow: () => toggleMenu(config.id, config.menuItemKeys),
            isStateless: true,

            // fallback strings
            ariaLabel: config.label,
            affordanceMenuItemText: config.label,
          });
        }
      });
    }
  }, [isShellReady, menuButtonConfigs, toggleMenu]);

  // initialize Office Shell
  useEffect(() => {
    // do not load Office Shell in WebView
    if (!isWebView) {
      loadOfficeShell(shellTheme, () => {
        setIsShellReady(true);
      });
    }
  }, [isWebView, shellTheme]);

  if (isWebView) {
    return null;
  }

  return (
    <>
      <div
        id="OneShellContainer"
        about={themeName} // eslint-disable-line react/no-unknown-property
        className={isMobile ? containerClassNames.mobile : containerClassNames.large}
      />
      <OfficeShellMenu
        menuButtonId={activeMenu?.menuButtonId}
        menuItemKeys={activeMenu?.menuItemKeys}
        hidden={!activeMenu}
        onHide={onHideMenu}
      />
      <OfficeShellPanel closeButtonAriaLabel={t('OfficeShellPanel.CloseButtonAriaLabel')} isOpen />
    </>
  );
};
